import React, { useEffect, useMemo, useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import {
  LayoutDashboard, Users, Building2, Wallet, UserCog, ScrollText,
  Building, CreditCard, CalendarClock, ShieldCheck,
} from 'lucide-react';
import { adminApi } from '@/lib/api';
import { TID } from '@/constants/testIds';
import { Loader } from '@/components/Loader';

const links = [
  { to: '/admin', end: true, label: 'Tableau de bord', icon: LayoutDashboard },
  { to: '/admin/bailleurs', label: 'Bailleurs', icon: Building2 },
  { to: '/admin/locataires', label: 'Locataires', icon: Users },
  { to: '/admin/agents', label: 'Agents', icon: UserCog },
  { to: '/admin/abonnements', label: 'Abonnements', icon: Wallet },
  { to: '/admin/catalogue', label: 'Paramètres', icon: UserCog },
  { to: '/admin/audit', label: 'Audit', icon: ScrollText },
];

function KpiCard({ label, value, sub, icon: Icon, tone = 'default', testid }) {
  const classes = tone === 'dark'
    ? 'bg-[#0D1B2A] text-white border-[#0D1B2A]'
    : tone === 'accent'
      ? 'bg-blue-50 border-blue-100 text-[#0D1B2A]'
      : 'bg-white border-slate-200 text-[#0D1B2A]';
  return (
    <div data-testid={testid} className={`rounded-2xl border p-5 ${classes}`}>
      <div className={`flex items-center justify-between text-[11px] font-semibold uppercase tracking-widest ${tone === 'dark' ? 'text-white/60' : 'text-slate-500'}`}><span>{label}</span>{Icon && <Icon className="h-4 w-4" />}</div>
      <div className="mt-3 text-3xl font-black">{value}</div>
      {sub && <div className={`mt-1 text-xs ${tone === 'dark' ? 'text-white/60' : 'text-slate-500'}`}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const [kpis, setKpis] = useState(null);
  const [bailleurs, setBailleurs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([adminApi.kpis(), adminApi.listBailleurs()])
      .then(([kpiResult, bailleurResult]) => {
        if (kpiResult.status === 'fulfilled') setKpis(kpiResult.value.data || {});
        if (bailleurResult.status === 'fulfilled') setBailleurs(bailleurResult.value.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeSubscriptions = useMemo(
    () => bailleurs.filter((b) => b.subscription?.status === 'active').length,
    [bailleurs]
  );
  const expiringSoon = useMemo(() => {
    const now = Date.now();
    const horizon = now + (7 * 24 * 60 * 60 * 1000);
    return bailleurs.filter((b) => {
      const value = b.subscription?.expires_at ? new Date(b.subscription.expires_at).getTime() : NaN;
      return Number.isFinite(value) && value >= now && value <= horizon;
    }).length;
  }, [bailleurs]);
  const expired = useMemo(() => {
    const now = Date.now();
    return bailleurs.filter((b) => {
      const value = b.subscription?.expires_at ? new Date(b.subscription.expires_at).getTime() : NaN;
      return b.subscription?.status === 'expired' || (Number.isFinite(value) && value < now);
    }).length;
  }, [bailleurs]);
  const mrr = activeSubscriptions * 3000;

  if (loading) {
    return <AppLayout links={links} testid={TID.adminSidebar} title="Vue globale de la plateforme"><Loader variant="full" /></AppLayout>;
  }

  return (
    <AppLayout links={links} testid={TID.adminSidebar} title="Vue globale de la plateforme">
      <p className="-mt-4 mb-6 text-sm text-slate-500">Pilotage LoyerBook : bailleurs, locataires, abonnements et échéances de renouvellement.</p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testid={TID.adminKpiBailleurs} label="Bailleurs actifs" icon={Building} value={kpis?.active_bailleurs ?? activeSubscriptions} sub={`${kpis?.total_bailleurs ?? bailleurs.length} au total`} tone="dark" />
        <KpiCard testid={TID.adminKpiLocataires} label="Locataires" icon={Users} value={kpis?.total_locataires ?? 0} sub="Aucune limite par bailleur" />
        <KpiCard label="Abonnements actifs" icon={ShieldCheck} value={activeSubscriptions} sub={`${expired} expiré(s)`} />
        <KpiCard testid={TID.adminKpiRevenue} label="MRR théorique" icon={CreditCard} value={`${mrr.toLocaleString('fr-FR')} FC`} sub="3 000 FC × abonnements actifs" tone="accent" />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="lb-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between gap-4"><div><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Modèle commercial</div><h2 className="mt-1 text-xl font-black text-[#0D1B2A]">Une seule offre LoyerBook</h2></div><div className="text-right"><div className="text-2xl font-black text-blue-600">3 000 FC</div><div className="text-xs text-slate-500">par mois</div></div></div>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-slate-50 p-4"><Users className="h-5 w-5 text-emerald-500" /><div className="mt-2 font-bold">Illimité</div><div className="text-xs text-slate-500">Locataires par bailleur</div></div>
            <div className="rounded-xl bg-slate-50 p-4"><CreditCard className="h-5 w-5 text-blue-500" /><div className="mt-2 font-bold">SasPay</div><div className="text-xs text-slate-500">Paiement & renouvellement</div></div>
            <div className="rounded-xl bg-slate-50 p-4"><CalendarClock className="h-5 w-5 text-amber-500" /><div className="mt-2 font-bold">Mensuel</div><div className="text-xs text-slate-500">Cycle de 30 jours</div></div>
          </div>
        </div>

        <div className="lb-card p-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">À surveiller</div>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3"><span className="text-sm text-slate-600">Expire sous 7 jours</span><strong className="text-amber-600">{expiringSoon}</strong></div>
            <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3"><span className="text-sm text-slate-600">Déjà expirés</span><strong className="text-red-600">{expired}</strong></div>
            <div className="flex items-center justify-between rounded-xl border border-slate-100 p-3"><span className="text-sm text-slate-600">Paiements loyers enregistrés</span><strong>{kpis?.total_paiements ?? 0}</strong></div>
          </div>
        </div>
      </div>

      <div className="mt-6 lb-card overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4"><div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Derniers bailleurs</div></div>
        <div className="divide-y divide-slate-100">
          {bailleurs.slice(0, 8).map((b) => (
            <div key={b.id} className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <div><div className="font-semibold text-[#0D1B2A]">{b.prenom} {b.nom}</div><div className="text-xs text-slate-500">{b.email}</div></div>
              <div className="flex items-center gap-4 text-xs text-slate-500"><span>{b.locataires_count || 0} locataire(s)</span><span className="font-semibold capitalize text-slate-700">{b.subscription?.status || 'sans abonnement'}</span></div>
            </div>
          ))}
          {!bailleurs.length && <div className="px-5 py-10 text-center text-sm text-slate-500">Aucun bailleur pour l’instant.</div>}
        </div>
      </div>
    </AppLayout>
  );
}
